/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListDataExportDestinationsRequest,
  ListDataExportDestinationsSecurity,
} from "../models/operations/listdataexportdestinations.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildDataExportDestinationsQuery,
  DataExportDestinationsQueryData,
  prefetchDataExportDestinations,
  queryKeyDataExportDestinations,
} from "./dataExportDestinations.core.js";
export {
  buildDataExportDestinationsQuery,
  type DataExportDestinationsQueryData,
  prefetchDataExportDestinations,
  queryKeyDataExportDestinations,
};

export type DataExportDestinationsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listDestinations dataExports
 *
 * @remarks
 * List data export destinations for the selected project.
 */
export function useDataExportDestinations(
  request?: ListDataExportDestinationsRequest | undefined,
  security?: ListDataExportDestinationsSecurity | undefined,
  options?: QueryHookOptions<
    DataExportDestinationsQueryData,
    DataExportDestinationsQueryError
  >,
): UseQueryResult<
  DataExportDestinationsQueryData,
  DataExportDestinationsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildDataExportDestinationsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listDestinations dataExports
 *
 * @remarks
 * List data export destinations for the selected project.
 */
export function useDataExportDestinationsSuspense(
  request?: ListDataExportDestinationsRequest | undefined,
  security?: ListDataExportDestinationsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    DataExportDestinationsQueryData,
    DataExportDestinationsQueryError
  >,
): UseSuspenseQueryResult<
  DataExportDestinationsQueryData,
  DataExportDestinationsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildDataExportDestinationsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setDataExportDestinationsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: DataExportDestinationsQueryData,
): DataExportDestinationsQueryData | undefined {
  const key = queryKeyDataExportDestinations(...queryKeyBase);

  return client.setQueryData<DataExportDestinationsQueryData>(key, data);
}

export function invalidateDataExportDestinations(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "dataExports",
      "listDestinations",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllDataExportDestinations(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "dataExports", "listDestinations"],
  });
}
